import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import { PrimaryButton } from '../components/PrimaryButton';

interface ConnectedApp {
  id: string;
  name: string;
  connected: boolean;
}

const initialApps: ConnectedApp[] = [
  { id: 'facebook', name: 'Facebook', connected: true },
  { id: 'google', name: 'Google', connected: false },
  { id: 'twitter', name: 'Twitter', connected: false },
  { id: 'instagram', name: 'Instagram', connected: false },
  { id: 'dropbox', name: 'Dropbox', connected: false },
];

export default function PrivacyPage() {
  const navigate = useNavigate();
  const [apps, setApps] = useState<ConnectedApp[]>(initialApps);

  const toggleConnection = (id: string) => {
    setApps(prev => prev.map(app =>
      app.id === id ? { ...app, connected: !app.connected } : app
    ));
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />

      <main className="flex-1 max-w-[1140px] mx-auto w-full px-4 py-12">
        {/* Back and Title */}
        <div className="flex items-start gap-4 mb-10">
          <Link
            to="/account-settings"
            className="mt-1 w-[40px] h-[40px] bg-[#3a3335]/[0.04] rounded-[10px] flex items-center justify-center hover:bg-[#3a3335]/[0.08] transition-colors shrink-0"
          >
            <span className="material-icons text-[#908e8e] text-[18px]">chevron_left</span>
          </Link>
          <div>
            <h1 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[18px] mb-1">
              Privacy
            </h1>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] tracking-[-0.1px]">
              Connected apps and services
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-col lg:flex-row justify-between gap-12">

          {/* Left Column */}
          <div className="flex-1 max-w-[553px]">
            <div className="space-y-0 divide-y divide-[#eee]">
              {apps.map((app) => (
                <div key={app.id} className="py-6 flex items-center justify-between group">
                  <div>
                    <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] tracking-[-0.1px] mb-1">
                      {app.name}
                    </p>
                    <p className={`font-['DM_Sans',sans-serif] font-bold text-[14px] tracking-[-0.1px] ${app.connected ? 'text-[#3a3335]' : 'text-[#3a3335]'}`}>
                      {app.connected ? 'Connected' : 'Not connected'}
                    </p>
                  </div>

                  <button
                    onClick={() => toggleConnection(app.id)}
                    className="flex items-center gap-2 text-[#908e8e] hover:text-[#8e2a2f] transition-colors font-['DM_Sans',sans-serif] font-bold text-[12px] tracking-[-0.1px]"
                  >
                    <span className="material-icons text-[18px]">
                      {app.connected ? 'link_off' : 'link'}
                    </span>
                    {app.connected ? 'Remove' : 'Connect'}
                  </button>
                </div>
              ))}
            </div>

            {/* Bottom Actions */}
            <div className="mt-12 flex items-center gap-4">
              <PrimaryButton className="w-auto px-6 h-[50px] text-[14px] whitespace-nowrap">
                Update Settings
              </PrimaryButton>
              <button
                onClick={() => navigate('/account-settings')}
                className="px-8 h-[50px] rounded-[10px] font-['DM_Sans',sans-serif] font-bold text-[#908e8e] text-[14px] bg-[#3a3335]/[0.04] hover:bg-[#3a3335]/[0.08] transition-all"
              >
                Cancel
              </button>
            </div>
          </div>

          {/* Right Info Card */}
          <div className="w-full lg:w-[361px] shrink-0">
            <div className="bg-white rounded-[6px] shadow-[0px_3px_4px_0px_rgba(153,155,168,0.15)] p-8">
              <div className="w-[48px] h-[48px] rounded-[8px] bg-[#3a3335]/[0.04] flex items-center justify-center mb-6">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_125_3267)">
                    <rect width="48" height="48" rx="8" fill="white" />
                    <rect width="48" height="48" rx="8" fill="#3A3335" fill-opacity="0.04" />
                    <path d="M22.0664 22.0664C22.6107 21.5221 23.2552 21.25 24 21.25C24.7448 21.25 25.3893 21.5221 25.9336 22.0664C26.4779 22.6107 26.75 23.2552 26.75 24C26.75 24.7448 26.4779 25.3893 25.9336 25.9336C25.3893 26.4779 24.7448 26.75 24 26.75C23.2552 26.75 22.6107 26.4779 22.0664 25.9336C21.5221 25.3893 21.25 24.7448 21.25 24C21.25 23.2552 21.5221 22.6107 22.0664 22.0664ZM20.7344 27.2656C21.651 28.1536 22.7396 28.5977 24 28.5977C25.2604 28.5977 26.3346 28.1536 27.2227 27.2656C28.1393 26.349 28.5977 25.2604 28.5977 24C28.5977 22.7396 28.1393 21.6654 27.2227 20.7773C26.3346 19.8607 25.2604 19.4023 24 19.4023C22.7396 19.4023 21.651 19.8607 20.7344 20.7773C19.8464 21.6654 19.4023 22.7396 19.4023 24C19.4023 25.2604 19.8464 26.349 20.7344 27.2656ZM17.8555 19.0156C19.6888 17.7552 21.737 17.125 24 17.125C26.263 17.125 28.3112 17.7552 30.1445 19.0156C31.9779 20.276 33.2956 21.9375 34.0977 24C33.2956 26.0625 31.9779 27.724 30.1445 28.9844C28.3112 30.2448 26.263 30.875 24 30.875C21.737 30.875 19.6888 30.2448 17.8555 28.9844C16.0221 27.724 14.7044 26.0625 13.9023 24C14.7044 21.9375 16.0221 20.276 17.8555 19.0156Z" fill="#908E8E" />
                  </g>
                  <defs>
                    <clipPath id="clip0_125_3267">
                      <rect width="48" height="48" fill="white" />
                    </clipPath>
                  </defs>
                </svg>

              </div>
              <h3 className="font-['Inter',sans-serif] font-bold text-[#3a3335] text-[16px] leading-[24px] mb-3">
                Setup profiles
              </h3>
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] leading-[21px] tracking-[-0.1px]">
                The following tips on creating a direct mail advertising campaign have been street-tested and will bring you huge returns
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
